import React from 'react';
import { XIcon, Clock, MapPin, User, Users, FileText, Edit, Trash2 } from 'lucide-react';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
interface TrainingDetailsModalProps {
  trainingSession: any;
  locations: any[];
  trainingTypes: any[];
  instructors: any[];
  onClose: () => void;
}
export function TrainingDetailsModal({
  trainingSession,
  locations,
  trainingTypes,
  instructors,
  onClose
}: TrainingDetailsModalProps) {
  // Find related data
  const location = locations.find(loc => loc.id === trainingSession.locationId);
  const trainingType = trainingTypes.find(type => type.id === trainingSession.trainingTypeId);
  const instructor = instructors.find(inst => inst.id === trainingSession.instructorId);
  // Format date (e.g., "Maandag 12 juni 2023")
  const formatDate = (date: Date) => {
    return date.toLocaleDateString('nl-NL', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };
  // Format time (e.g., "09:00")
  const formatTime = (date: Date) => {
    return date.toLocaleTimeString('nl-NL', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  };
  // Calculate duration in hours and minutes
  const calculateDuration = () => {
    const start = new Date(trainingSession.startTime);
    const end = new Date(trainingSession.endTime);
    const diffMs = end.getTime() - start.getTime();
    const diffHrs = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMins = Math.round(diffMs % (1000 * 60 * 60) / (1000 * 60));
    if (diffHrs === 0) {
      return `${diffMins} minuten`;
    } else if (diffMins === 0) {
      return `${diffHrs} uur`;
    } else {
      return `${diffHrs} uur en ${diffMins} minuten`;
    }
  };
  return <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-id="element-3122">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg" data-id="element-3123">
        {/* Header */}
        <div className="flex justify-between items-center p-4 border-b border-gray-200" data-id="element-3124">
          <h3 className="text-lg font-medium text-gray-900" data-id="element-3125">
            Training Details
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-500" data-id="element-3126">
            <XIcon className="h-5 w-5" data-id="element-3127" />
          </button>
        </div>
        {/* Content */}
        <div className="p-6 space-y-4" data-id="element-3128">
          <div className="flex items-start" data-id="element-3129">
            <div className={`p-3 rounded-lg mr-4 bg-${trainingType.color}-100`} data-id="element-3130">
              <div className={`h-8 w-8 rounded-full bg-${trainingType.color}-500 flex items-center justify-center text-white font-bold`} data-id="element-3131">
                {trainingType.name.charAt(0)}
              </div>
            </div>
            <div data-id="element-3132">
              <h4 className="text-xl font-semibold text-gray-900" data-id="element-3133">
                {trainingType.name}
              </h4>
              <Badge variant={trainingType.color === 'blue' ? 'info' : trainingType.color === 'green' ? 'success' : trainingType.color === 'red' ? 'danger' : trainingType.color === 'yellow' ? 'warning' : 'default'} data-id="element-3134">
                {trainingType.duration} minuten
              </Badge>
            </div>
          </div>
          <div className="grid grid-cols-1 gap-4 pt-2" data-id="element-3135">
            <div className="flex items-center" data-id="element-3136">
              <Clock className="h-5 w-5 text-gray-400 mr-2" data-id="element-3137" />
              <div data-id="element-3138">
                <p className="text-sm font-medium text-gray-900" data-id="element-3139">
                  {formatDate(trainingSession.startTime)}
                </p>
                <p className="text-sm text-gray-500" data-id="element-3140">
                  {formatTime(trainingSession.startTime)} -{' '}
                  {formatTime(trainingSession.endTime)} ({calculateDuration()})
                </p>
              </div>
            </div>
            <div className="flex items-center" data-id="element-3141">
              <MapPin className="h-5 w-5 text-gray-400 mr-2" data-id="element-3142" />
              <div data-id="element-3143">
                <p className="text-sm font-medium text-gray-900" data-id="element-3144">
                  {location.name}
                </p>
                <p className="text-sm text-gray-500" data-id="element-3145">{location.address}</p>
              </div>
            </div>
            <div className="flex items-center" data-id="element-3146">
              <User className="h-5 w-5 text-gray-400 mr-2" data-id="element-3147" />
              <div data-id="element-3148">
                <p className="text-sm font-medium text-gray-900" data-id="element-3149">
                  Instructeur: {instructor.name}
                </p>
              </div>
            </div>
            <div className="flex items-center" data-id="element-3150">
              <Users className="h-5 w-5 text-gray-400 mr-2" data-id="element-3151" />
              <div data-id="element-3152">
                <p className="text-sm font-medium text-gray-900" data-id="element-3153">
                  {trainingSession.participants} deelnemers
                </p>
              </div>
            </div>
            {trainingSession.notes && <div className="flex items-start pt-2" data-id="element-3154">
                <FileText className="h-5 w-5 text-gray-400 mr-2 mt-0.5" data-id="element-3155" />
                <div data-id="element-3156">
                  <p className="text-sm font-medium text-gray-900 mb-1" data-id="element-3157">
                    Notities
                  </p>
                  <p className="text-sm text-gray-600" data-id="element-3158">
                    {trainingSession.notes}
                  </p>
                </div>
              </div>}
          </div>
        </div>
        {/* Footer */}
        <div className="flex justify-between items-center p-4 border-t border-gray-200 bg-gray-50 rounded-b-lg" data-id="element-3159">
          <Button variant="outline" size="sm" className="text-red-600 border-red-300 hover:bg-red-50" data-id="element-3160">
            <Trash2 className="h-4 w-4 mr-1" data-id="element-3161" />
            Verwijderen
          </Button>
          <div className="flex space-x-2" data-id="element-3162">
            <Button variant="outline" size="sm" onClick={onClose} data-id="element-3163">
              Sluiten
            </Button>
            <Button variant="primary" size="sm" data-id="element-3164">
              <Edit className="h-4 w-4 mr-1" data-id="element-3165" />
              Bewerken
            </Button>
          </div>
        </div>
      </div>
    </div>;
}